import { Component, Element, Event, EventEmitter, Host, Listen, Prop, State, h } from '@stencil/core';

import { getLocalizedStrings, isAlphabeticKey } from '../../utils/utils';
import rpfLanguagePickerString from './locales/translations.json';

@Component({
  tag: 'rpf-language-picker',
  styleUrl: 'rpf-language-picker.scss',
  shadow: true,
})
export class RpfLanguagePicker {
  @Element() el: HTMLElement;
  @Prop() locale: string = 'en-GB';
  @Prop() locales: string;
  @Event() selectLanguage: EventEmitter<{ event: globalThis.Event; value: string }>;
  @Prop({ reflect: true }) theme: string;
  @State() selectedOption: string = this.locale;
  @State() isOpen: boolean = false;

  @Listen('keydown')
  handleKeyDown(event: KeyboardEvent) {
    const { key } = event;

    if (['ArrowDown', 'ArrowUp'].includes(key)) {
      this.shiftFocusOnArrowKeyInput(key);
    } else if (isAlphabeticKey(key)) {
      this.handleAlphabeticKeyPress(key);
    }
  }

  @Listen('isOpenChange')
  isOpenChangeHandler(event: CustomEvent<{ isOpen: boolean; char?: string }>) {
    this.isOpen = event.detail.isOpen;
    if (event.detail?.char) {
      this.lastKey = event.detail.char;
    }
  }

  languageString: any;
  localesData: any;
  buttonText: string;
  anchors: Array<HTMLAnchorElement>;
  focusIndex: number = 0;
  lastKey: string = '';
  lastKeyTime: number = 0;

  shiftFocusOnArrowKeyInput(key: string) {
    const { focusIndex, anchors } = this;

    if (key === 'ArrowDown') {
      this.focusIndex = focusIndex < anchors.length - 1 ? focusIndex + 1 : anchors.length - 1;
    } else if (key === 'ArrowUp') {
      this.focusIndex = focusIndex > 0 ? focusIndex - 1 : 0;
    }
    anchors[this.focusIndex].focus();
  }

  handleAlphabeticKeyPress(key: string) {
    let searchString = key.toLowerCase();

    if (Date.now() - this.lastKeyTime < 1000) {
      searchString = this.lastKey + searchString;
    }

    const index = this.anchors.findIndex(anchor => anchor.textContent.trim().toLowerCase().startsWith(searchString));
    if (index !== -1) {
      this.focusIndex = index;
    }

    this.lastKey = searchString;
    this.lastKeyTime = Date.now();
    this.anchors[this.focusIndex].focus();
  }

  cleanUpKeyPress() {
    this.lastKey = '';
    this.lastKeyTime = 0;
  }

  selectLanguageHandler(event, key: string) {
    this.selectLanguage.emit({ event: event, value: key });
  }

  handleFocus() {
    this.focusIndex = this.anchors.findIndex(anchor => anchor.classList.contains('selected'));
    if (this.lastKey) {
      this.handleAlphabeticKeyPress(this.lastKey);
    } else {
      this.focusIndex = this.focusIndex === -1 ? 0 : this.focusIndex;
    }
    this.anchors[this.focusIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /* ****************************************** */
  /* Lifecycle Methods */
  /* ****************************************** */

  componentWillRender() {
    this.languageString = getLocalizedStrings(this.locale, rpfLanguagePickerString);
    try {
      this.localesData = JSON.parse(this.locales);
      const localeData = this.localesData[this.locale] || this.localesData['en-GB'];
      this.buttonText = localeData['text'];
    } catch (error) {}
  }

  componentDidRender() {
    this.anchors = Array.from(this.el.shadowRoot.querySelectorAll('ul li a'));
  }

  componentDidUpdate() {
    this.isOpen ? this.handleFocus() : this.cleanUpKeyPress();
  }

  render() {
    if (this.localesData == undefined) {
      return;
    }

    return (
      <Host>
        <rpf-dropdown align-menu="center" theme={this.theme} selected={`${this.locale}-locale-option`} list-box-id="rpf-language-picker__listbox">
          <div slot="button">
            <div class="rpf-language-picker__button">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M8.99984 17.3333C7.86095 17.3333 6.78456 17.1144 5.77067 16.6767C4.75678 16.2394 3.8715 15.6422 3.11484 14.885C2.35762 14.1283 1.76039 13.243 1.32317 12.2292C0.885393 11.2153 0.666504 10.1389 0.666504 8.99999C0.666504 7.84721 0.885393 6.76749 1.32317 5.76082C1.76039 4.7536 2.35762 3.87166 3.11484 3.11499C3.8715 2.35777 4.75678 1.76027 5.77067 1.32249C6.78456 0.885268 7.86095 0.666656 8.99984 0.666656C10.1526 0.666656 11.2323 0.885268 12.239 1.32249C13.2462 1.76027 14.1282 2.35777 14.8848 3.11499C15.6421 3.87166 16.2393 4.7536 16.6765 5.76082C17.1143 6.76749 17.3332 7.84721 17.3332 8.99999C17.3332 10.1389 17.1143 11.2153 16.6765 12.2292C16.2393 13.243 15.6421 14.1283 14.8848 14.885C14.1282 15.6422 13.2462 16.2394 12.239 16.6767C11.2323 17.1144 10.1526 17.3333 8.99984 17.3333ZM12.2915 5.66666H14.7498C14.3471 4.97221 13.8434 4.36805 13.239 3.85416C12.6351 3.34027 11.9443 2.95832 11.1665 2.70832C11.4165 3.16666 11.6354 3.64221 11.8232 4.13499C12.0104 4.62832 12.1665 5.13888 12.2915 5.66666ZM7.4165 5.66666H10.5832C10.4165 5.05555 10.2012 4.47916 9.93734 3.93749C9.67345 3.39582 9.36095 2.87499 8.99984 2.37499C8.63873 2.87499 8.32623 3.39582 8.06234 3.93749C7.79845 4.47916 7.58317 5.05555 7.4165 5.66666ZM2.5415 10.6667H5.37484C5.33317 10.3889 5.30178 10.1144 5.28067 9.84332C5.26012 9.57277 5.24984 9.29166 5.24984 8.99999C5.24984 8.70832 5.26012 8.42721 5.28067 8.15666C5.30178 7.88555 5.33317 7.6111 5.37484 7.33332H2.5415C2.47206 7.6111 2.41984 7.88555 2.38484 8.15666C2.35039 8.42721 2.33317 8.70832 2.33317 8.99999C2.33317 9.29166 2.35039 9.57277 2.38484 9.84332C2.41984 10.1144 2.47206 10.3889 2.5415 10.6667ZM6.83317 15.2917C6.58317 14.8333 6.36456 14.3575 6.17734 13.8642C5.98956 13.3714 5.83317 12.8611 5.70817 12.3333H3.24984C3.65262 13.0278 4.15595 13.6319 4.75984 14.1458C5.36428 14.6597 6.05539 15.0417 6.83317 15.2917ZM3.24984 5.66666H5.70817C5.83317 5.13888 5.98956 4.62832 6.17734 4.13499C6.36456 3.64221 6.58317 3.16666 6.83317 2.70832C6.05539 2.95832 5.36428 3.34027 4.75984 3.85416C4.15595 4.36805 3.65262 4.97221 3.24984 5.66666ZM8.99984 15.625C9.36095 15.125 9.67345 14.6042 9.93734 14.0625C10.2012 13.5208 10.4165 12.9444 10.5832 12.3333H7.4165C7.58317 12.9444 7.79845 13.5208 8.06234 14.0625C8.32623 14.6042 8.63873 15.125 8.99984 15.625ZM7.0415 10.6667H10.9582C10.9998 10.3889 11.0312 10.1144 11.0523 9.84332C11.0729 9.57277 11.0832 9.29166 11.0832 8.99999C11.0832 8.70832 11.0729 8.42721 11.0523 8.15666C11.0312 7.88555 10.9998 7.6111 10.9582 7.33332H7.0415C6.99984 7.6111 6.96873 7.88555 6.94817 8.15666C6.92706 8.42721 6.9165 8.70832 6.9165 8.99999C6.9165 9.29166 6.92706 9.57277 6.94817 9.84332C6.96873 10.1144 6.99984 10.3889 7.0415 10.6667ZM11.1665 15.2917C11.9443 15.0417 12.6351 14.6597 13.239 14.1458C13.8434 13.6319 14.3471 13.0278 14.7498 12.3333H12.2915C12.1665 12.8611 12.0104 13.3714 11.8232 13.8642C11.6354 14.3575 11.4165 14.8333 11.1665 15.2917ZM12.6248 10.6667H15.4582C15.5276 10.3889 15.5798 10.1144 15.6148 9.84332C15.6493 9.57277 15.6665 9.29166 15.6665 8.99999C15.6665 8.70832 15.6493 8.42721 15.6148 8.15666C15.5798 7.88555 15.5276 7.6111 15.4582 7.33332H12.6248C12.6665 7.6111 12.6976 7.88555 12.7182 8.15666C12.7393 8.42721 12.7498 8.70832 12.7498 8.99999C12.7498 9.29166 12.7393 9.57277 12.7182 9.84332C12.6976 10.1144 12.6665 10.3889 12.6248 10.6667Z"
                  fill="#212121"
                />
              </svg>
              <span class="rpf-language-picker__button-text">{this.buttonText}</span>
            </div>
          </div>
          <div class="rpf-language-picker__menu">
            <h2>{this.languageString['language']}</h2>
            <ul>
              {Object.entries(this.localesData).map(([key, value]) => (
                <li>
                  <a
                    href={value['url'] || '#'}
                    onClick={e => this.selectLanguageHandler(e, key)}
                    role={value['url'] ? 'option' : 'button'}
                    class={`${this.locale === key && 'selected'}`}
                    aria-selected={this.locale === key ? 'true' : 'false'}
                    id={`${key}-locale-option`}
                  >
                    {this.locale === key && (
                      <svg width="18" height="13" viewBox="0 0 18 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          class="rpf-language-picker__tick"
                          d="M6.5501 13L0.850098 7.29998L2.2751 5.87498L6.5501 10.15L15.7251 0.974976L17.1501 2.39998L6.5501 13Z"
                          fill="#212121"
                        />
                      </svg>
                    )}
                    {value['text']}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </rpf-dropdown>
      </Host>
    );
  }
}
